import { messages } from '@/lib/messages'

/**
 * Biçimleme kuralları (sözleşme §5 ve §10):
 * - Sayı biçimi `tr-TR`.
 * - `tarih` kolonu **date** tiplidir; takvim günüdür, saat dilimi dönüşümü uygulanmaz.
 * - Timestamp kolonları API'den offset'li ISO-8601 gelir; gösterim `Europe/Istanbul`.
 */
export const DISPLAY_TIME_ZONE = 'Europe/Istanbul'
const LOCALE = 'tr-TR'

const integerFormatter = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 })

const decimalFormatters = new Map<number, Intl.NumberFormat>()

function decimalFormatter(digits: number): Intl.NumberFormat {
  let formatter = decimalFormatters.get(digits)
  if (!formatter) {
    formatter = new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
    decimalFormatters.set(digits, formatter)
  }
  return formatter
}

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

const timeFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: DISPLAY_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

export const EMPTY_VALUE = messages.common.empty

export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

export function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE
  return integerFormatter.format(value)
}

const patternFormatters = new Map<string, Intl.NumberFormat>()

/**
 * Cube meta'daki Excel sayı biçim kodunu (`#,##0.000`, `#,##0.###`, `0`) ekranda
 * uygular: `,` binlik ayırıcı, `.` sonrası `0` zorunlu, `#` isteğe bağlı hane.
 * Excel aktarımı aynı kodu hücreye yazar; ekran ile dosya böylece ayrışmaz.
 */
export function formatNumberPattern(value: number | null | undefined, pattern: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE
  let formatter = patternFormatters.get(pattern)
  if (!formatter) {
    const decimals = pattern.split('.')[1] ?? ''
    const minimumFractionDigits = (decimals.match(/0/g) ?? []).length
    formatter = new Intl.NumberFormat(LOCALE, {
      useGrouping: pattern.includes(','),
      minimumFractionDigits,
      maximumFractionDigits: minimumFractionDigits + (decimals.match(/#/g) ?? []).length,
    })
    patternFormatters.set(pattern, formatter)
  }
  return formatter.format(value)
}

export function formatDecimal(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE
  return decimalFormatter(digits).format(value)
}

/**
 * `date` tipli kolonlar (ör. `tarih`): `YYYY-MM-DD` dizesi olduğu gibi parçalanır.
 * `new Date(...)` kullanılmaz — saat dilimi kayması takvim gününü bir gün oynatabilir.
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (isBlank(value)) return EMPTY_VALUE
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value as string)
  if (!match) return value as string
  return `${match[3]}.${match[2]}.${match[1]}`
}

export function formatDateTime(value: string | null | undefined): string {
  if (isBlank(value)) return EMPTY_VALUE
  const date = new Date(value as string)
  if (Number.isNaN(date.getTime())) return value as string
  return dateTimeFormatter.format(date)
}

const CUBE_DATETIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/

/**
 * Cube'un offset'siz döndürdüğü zaman dizeleri için (`2026-09-22T14:30:00.000`) —
 * bu dize zaten İstanbul duvar saatidir (bkz. §1.4 tuzağı, `cube/model/kantar/_tartim_base.yml`
 * içindeki `AT TIME ZONE` dönüşümü). `new Date(...)` **kullanılmaz**: offset'siz bir
 * dizeyi tarayıcının kendi saat dilimine göre yorumlar, bu da İstanbul dışındaki bir
 * tarayıcıda saati kaydırır. Bunun yerine basamaklar doğrudan metinden okunur.
 */
export function formatCubeDateTime(value: string | null | undefined): string {
  if (isBlank(value)) return EMPTY_VALUE
  const match = CUBE_DATETIME_PATTERN.exec(value as string)
  if (!match) return value as string
  const [, year, month, day, hour, minute, second] = match
  return `${day}.${month}.${year} ${hour}:${minute}:${second}`
}

/**
 * Cube zaman kovası (`2026-09-01T00:00:00.000`, İstanbul duvar saati) -> kırılıma
 * göre etiket. Metinden okunur, `new Date` kullanılmaz (bkz. `formatCubeDateTime`).
 */
export function formatTimeBucket(
  value: string | null | undefined,
  granularity: 'day' | 'week' | 'month' | 'quarter' | 'year',
): string {
  if (isBlank(value)) return EMPTY_VALUE
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value as string)
  if (!match) return value as string
  const [, year, month, day] = match
  switch (granularity) {
    case 'year':
      return year
    case 'quarter':
      return messages.report.quarterLabel(year, Math.floor((Number(month) - 1) / 3) + 1)
    case 'month':
      return `${month}.${year}`
    default:
      return `${day}.${month}.${year}`
  }
}

export function formatTime(value: string | null | undefined): string {
  if (isBlank(value)) return EMPTY_VALUE
  const date = new Date(value as string)
  if (Number.isNaN(date.getTime())) return value as string
  return timeFormatter.format(date)
}

export function formatBoolean(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return EMPTY_VALUE
  return value ? messages.common.yes : messages.common.no
}

export function formatText(value: string | number | null | undefined): string {
  if (isBlank(value)) return EMPTY_VALUE
  return String(value)
}

/** Geçen süre, en büyük iki birimle: `45 dk`, `5 sa 20 dk`, `2 gün 3 sa` (aşağı yuvarlanır). */
export function formatDuration(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000))
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const rest = minutes % 60
  const { day, hour, minute } = messages.common.duration
  if (days > 0) return hours > 0 ? `${days} ${day} ${hours} ${hour}` : `${days} ${day}`
  if (hours > 0) return rest > 0 ? `${hours} ${hour} ${rest} ${minute}` : `${hours} ${hour}`
  return `${rest} ${minute}`
}

/** `<input type="date">` için bugünün tarihi, tesis yerel takvim günü olarak. */
export function todayAsDateInput(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPLAY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
  return parts
}

/** Para birimi kodu (kaynaktaki `TL`/`USD` gibi) -> ekran simgesi; bilinmeyen kod metnin sonuna. */
const CURRENCY_SYMBOLS: Record<string, string> = { TL: '₺', TRY: '₺', USD: '$', EUR: '€', GBP: '£' }

export function withCurrency(text: string, code: string | null | undefined): string {
  if (!code || text === EMPTY_VALUE) return text
  const symbol = CURRENCY_SYMBOLS[code]
  if (!symbol) return `${text} ${code}`
  return text.startsWith('-') ? `-${symbol}${text.slice(1)}` : `${symbol}${text}`
}

/** Ay ekler; hedef ayda o gün yoksa ayın son günü (31 Mart - 1 ay = 28 Şubat). */
function shiftDateInputMonths(value: string, months: number): string {
  const [y, m, d] = value.split('-').map(Number)
  const month = (m ?? 1) - 1 + months
  const lastDay = new Date(Date.UTC(y, month + 1, 0)).getUTCDate()
  return new Date(Date.UTC(y, month, Math.min(d ?? 1, lastDay))).toISOString().slice(0, 10)
}

export const RELATIVE_RANGE_PATTERN = /^([+-])(\d+)([dmy])$/

/**
 * Göreli tarih aralığı, her iki uç dahil: `-1y` son 1 yıl (bugün biter), `+2y`
 * önümüzdeki 2 yıl (bugün başlar). Birim `d`/`m`/`y`. `-7d` mevcut "son 7 gün"
 * ile aynı aralığı verir.
 */
export function relativeRange(token: string, today = todayAsDateInput()) {
  const match = RELATIVE_RANGE_PATTERN.exec(token)
  if (!match) throw new Error(`Geçersiz göreli aralık: ${token}`)
  const [, sign, amount, unit] = match
  const n = Number(amount) * (sign === '-' ? -1 : 1)
  const edge =
    unit === 'd' ? shiftDateInput(today, n) : shiftDateInputMonths(today, unit === 'y' ? n * 12 : n)
  return sign === '-'
    ? { from: shiftDateInput(edge, 1), to: today }
    : { from: today, to: shiftDateInput(edge, -1) }
}

/** Verilen takvim gününden `days` gün öncesi; yalnızca `YYYY-MM-DD` aritmetiği. */
export function shiftDateInput(value: string, days: number): string {
  const [y, m, d] = value.split('-').map(Number)
  const utc = Date.UTC(y, (m ?? 1) - 1, d ?? 1)
  const shifted = new Date(utc + days * 86_400_000)
  return shifted.toISOString().slice(0, 10)
}

/** `(şimdi - önce) / önce` yüzde; önceki dönem 0 ya da boşsa hesaplanmaz. */
export function changePercent(current: unknown, previous: unknown): number | undefined {
  if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) {
    return undefined
  }
  return ((current - previous) / Math.abs(previous)) * 100
}

/** İşaretli yüzde metni: `+12,4%`. */
export function formatChange(percent: number): string {
  return `${percent > 0 ? '+' : ''}${formatNumberPattern(percent, '0.0')}%`
}
